// Session freshness guard for client paths that hit RLS / JWT-verified
// surfaces after the user spent a long time away from the app shell — the
// canonical case being the MetaPerson Creator iframe (5–10 min inside it and
// the access token can expire; the WKWebView may also reload under the
// creator's WebGL memory pressure). Storage uploads with a dead session fail
// as RLS violations with auth.uid() = null (Sentry VYV-FRONTEND-P), and
// JWT-verified edge functions answer 401.
//
// Returns true when a usable session exists after the check (refreshing it
// when it is missing or about to expire); false when the user is genuinely
// signed out. Never throws.

import { supabase } from "@/integrations/supabase/client";

// Refresh when fewer than this many seconds remain — covers the request's
// own flight time plus server clock skew.
const EXPIRY_MARGIN_S = 60;

export async function ensureFreshSession(): Promise<boolean> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const expiresAt = session?.expires_at ?? 0;
    const nowS = Math.floor(Date.now() / 1000);
    if (session && expiresAt - nowS > EXPIRY_MARGIN_S) return true;

    // Missing or near-expiry → one explicit refresh attempt.
    const { data, error } = await supabase.auth.refreshSession();
    if (error || !data.session) {
      console.error("[authSession] refresh failed:", error ?? "no session");
      return false;
    }
    return true;
  } catch (err) {
    console.error("[authSession] ensureFreshSession threw:", err);
    return false;
  }
}
